import { memo } from 'react';

import { Link } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarFallback, AvatarImage, TableCell, TableRow } from '@/_components';
import { PhoneNumbersLinker, SortableList } from '@/_containers';
import { nameResolver } from '@/_shared';

import type { ContactLinkerWithPhoneNumberPivotQuerySchema } from '../../schemas';

type ClientContactItemProps = {
  item: ContactLinkerWithPhoneNumberPivotQuerySchema;
  removeItemHandler: (item: ContactLinkerWithPhoneNumberPivotQuerySchema) => void;
  isEditable?: boolean;
  className?: string;
  showPrimaryContactAction?: boolean;
  isPrimary?: boolean;
  hasPrimary?: boolean;
  sortable?: boolean;
  asTableRow?: boolean;
  requireEmail?: boolean;
  setContactType?: (item: ContactLinkerWithPhoneNumberPivotQuerySchema, isPrimary: boolean) => void;
};

function ItemWithPhoneNumberBase({
  item,
  isEditable = false,
  removeItemHandler,
  className,
  showPrimaryContactAction = false,
  isPrimary = false,
  hasPrimary = false,
  sortable = false,
  asTableRow = false,
  requireEmail,
  setContactType,
}: ClientContactItemProps) {
  const [primaryEmail] = item?.emails ?? [];

  const tableSizing = requireEmail ? 'w-1/4' : 'w-/13';
  const elementSizing = requireEmail ? 'sm:w-1/4 sm:max-w-1/4 sm:min-w-1/4' : 'sm:w-1/3 sm:max-w-1/3 sm:min-w-1/3';

  const nameElement = (
    <Link
      to="/contacts/$id"
      params={{ id: `${item.id}` }}
      state={{ naturalBack: true }}
      className="group flex items-center gap-1.5"
    >
      <Avatar>
        <AvatarImage src={item.profile_image?.[0]?.url} />
        <AvatarFallback name={nameResolver(item.first_name, item.last_name)} />
      </Avatar>

      <span className="decoration-primary text-sm decoration-2 group-hover:underline">
        {nameResolver(item.first_name, item.last_name)}
      </span>
    </Link>
  );

  const titleElement = <div className="text-c-card-muted line-clamp-1 text-sm">{item?.title ?? '—'}</div>;
  const emailElement = <div className="text-c-card-muted line-clamp-1 text-sm">{primaryEmail?.email ?? '-'}</div>;

  const phoneElement = (
    <PhoneNumbersLinker
      phone_numbers={item.phone_numbers}
      isEditable={isEditable}
      removeItemHandler={() => removeItemHandler(item)}
      showPrimaryContactAction={showPrimaryContactAction}
      isPrimary={isPrimary}
      hasPrimary={hasPrimary}
      setContactType={() => setContactType?.(item, isPrimary)}
      phoneNumberClassName="w-full max-w-full"
      phoneNumberContainerClassName="w-full max-w-full"
    />
  );

  const BaseItem = asTableRow ? (
    <TableRow allowSelectionStyle={false} className={twMerge('text-accent align-top dark:text-gray-400', className)}>
      <TableCell className={twMerge('align-center', tableSizing)}>
        <div className="line-clamp-1">{nameElement}</div>
      </TableCell>
      <TableCell className={twMerge('align-center', tableSizing)}>{titleElement}</TableCell>
      {requireEmail && <TableCell className={twMerge('align-center', tableSizing)}>{emailElement}</TableCell>}
      <TableCell className={twMerge('align-center', tableSizing)}>{phoneElement}</TableCell>
    </TableRow>
  ) : (
    <section
      className={twMerge(
        'border-c-border-muted text-accent flex min-h-11 max-w-full flex-1 flex-col flex-nowrap items-start justify-between gap-1.5 border-b py-2 sm:flex-row dark:text-gray-400',
        className
      )}
    >
      <div className={twMerge('line-clamp-1 w-full', elementSizing)}>{nameElement}</div>

      <div className={twMerge('flex min-h-8 w-full items-center', elementSizing)}>{titleElement}</div>

      {requireEmail && <div className={twMerge('flex min-h-8 w-full items-center', elementSizing)}>{emailElement}</div>}
      <div className={twMerge('w-full', elementSizing)}>{phoneElement}</div>
    </section>
  );

  return sortable && !asTableRow ? (
    <SortableList.Item key={item.id} id={item.id} className="items-center!">
      {isEditable && <SortableList.DragHandle className="mr-1! mb-0.5!" />}
      {BaseItem}
    </SortableList.Item>
  ) : (
    BaseItem
  );
}

export const ItemWithPhoneNumber = memo(ItemWithPhoneNumberBase);
